import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { motion } from 'framer-motion';
import {
    Check,
    ArrowRight,
    AlertCircle,
    Coins,
    Pill,
    Trash2,
    ShieldCheck,
    Stethoscope,
    Baby,
    Smile,
    Glasses,
    Scale,
    Lock,
    AlertTriangle,
    FileText,
    ExternalLink
} from 'lucide-react';
import './RichTextRenderer.css';

// Icon mapping logic based on header text
const getHeaderIcon = (text) => {
    if (!text) return ArrowRight;
    const titleUpper = text.toString().toUpperCase();

    if (titleUpper.includes('FUNDING') || titleUpper.includes('COST') || titleUpper.includes('PAY') || titleUpper.includes('MONEY') || titleUpper.includes('FINANCIAL') || titleUpper.includes('PROFIT')) return Coins;
    if (titleUpper.includes('DRUG') || titleUpper.includes('MEDICINE') || titleUpper.includes('PHARMA') || titleUpper.includes('PRESCRIPTION')) return Pill;
    if (titleUpper.includes('WASTE') || titleUpper.includes('ADMIN') || titleUpper.includes('PAPERWORK') || titleUpper.includes('BUREAUCRACY')) return Trash2;
    if (titleUpper.includes('COVER') || titleUpper.includes('SERVICE') || titleUpper.includes('BENEFIT') || titleUpper.includes('PROTECT') || titleUpper.includes('INSURANCE')) return ShieldCheck;
    if (titleUpper.includes('DOCTOR') || titleUpper.includes('PROVIDER') || titleUpper.includes('HOSPITAL') || titleUpper.includes('CARE') || titleUpper.includes('CLINIC')) return Stethoscope;
    if (titleUpper.includes('CHILD') || titleUpper.includes('KID') || titleUpper.includes('BABY') || titleUpper.includes('FAMILY')) return Baby;
    if (titleUpper.includes('DENTAL') || titleUpper.includes('TOOTH') || titleUpper.includes('TEETH')) return Smile;
    if (titleUpper.includes('VISION') || titleUpper.includes('EYE') || titleUpper.includes('SIGHT')) return Glasses;
    if (titleUpper.includes('LAW') || titleUpper.includes('LEGAL') || titleUpper.includes('RULE') || titleUpper.includes('REGULATION') || titleUpper.includes('ACT')) return Scale;
    if (titleUpper.includes('FRAUD') || titleUpper.includes('SCAM') || titleUpper.includes('THEFT') || titleUpper.includes('TRAP')) return Lock;
    if (titleUpper.includes('RISK') || titleUpper.includes('DANGER') || titleUpper.includes('WARNING') || titleUpper.includes('PROBLEM') || titleUpper.includes('CRISIS')) return AlertTriangle;
    if (titleUpper.includes('SUMMARY') || titleUpper.includes('NOTE')) return FileText;

    // Type/Tone checks
    if (titleUpper.includes('REALITY') || titleUpper.includes('FACT') || titleUpper.includes('SOLUTION') || titleUpper.includes('FIX')) return Check;
    if (titleUpper.includes('MYTH') || titleUpper.includes('LIE') || titleUpper.includes('CLAIM')) return AlertCircle;

    return ArrowRight;
};

const RichTextRenderer = ({ content, className = '' }) => {
    // Handle array input (legacy support + convenient JSON editing)
    let markdownContent = Array.isArray(content) ? content.join('\n\n') : content;

    // Global replacement for brand formatting: SAFECare+ or SAFEC+ -> SAFE(BoldItalic C)(RedBold +)
    if (markdownContent) {
        markdownContent = markdownContent.replace(/SAFEC(?:are)?\+/g, 'SAFE<b><i>C</i></b><span style="color: #ef4444; font-weight: 800;">+</span>');
    }

    if (!markdownContent) return null;

    // Custom components for ReactMarkdown
    const components = {
        p: ({ node, ...props }) => <p {...props} />,
        h1: ({ children }) => {
            const Icon = getHeaderIcon(children);
            return (
                <div className="rt-header-wrapper level-1">
                    <Icon className="rt-header-icon color-accent" size={28} />
                    <h1>{children}</h1>
                    <div className="rt-header-accent"></div>
                </div>
            );
        },
        h2: ({ children }) => {
            const Icon = getHeaderIcon(children);
            return (
                <div className="rt-header-wrapper level-2">
                    <Icon className="rt-header-icon color-accent" size={24} />
                    <h2>{children}</h2>
                    <div className="rt-header-accent"></div>
                </div>
            );
        },
        h3: ({ children }) => {
            const Icon = getHeaderIcon(children);
            return (
                <div className="rt-header-wrapper level-3">
                    <Icon className="rt-header-icon color-accent" size={20} />
                    <h3>{children}</h3>
                </div>
            );
        },
        ul: ({ children }) => <ul className="rt-list">{children}</ul>,
        ol: ({ children }) => <ol className="rt-list rt-list-ordered">{children}</ol>,
        li: ({ children }) => (
            <motion.li
                initial={{ opacity: 0, x: -10 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.3 }}
            >
                <div className="rt-list-marker">
                    <ArrowRight size={14} className="rt-arrow-icon" />
                </div>
                <span className="rt-list-text">{children}</span>
            </motion.li>
        ),
        blockquote: ({ children }) => (
            <blockquote className="rt-blockquote">
                <div className="rt-blockquote-bar" />
                <div className="rt-blockquote-content">{children}</div>
            </blockquote>
        ),
        a: ({ href, children }) => (
            <a href={href} target="_blank" rel="noopener noreferrer" className="rt-link">
                {children} <ExternalLink size={12} style={{ display: 'inline', marginLeft: 4 }} />
            </a>
        ),
        table: ({ children }) => (
            <div className="rt-table-wrapper">
                <table className="rt-table">{children}</table>
            </div>
        )
    };

    return (
        <div className={`rich-text-container ${className}`}>
            <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                rehypePlugins={[rehypeRaw]}
                components={components}
            >
                {markdownContent}
            </ReactMarkdown>
        </div>
    );
};

export default RichTextRenderer;
